/**
 * Entry point of the index side (builder B, PLAN §13): the client for this
 * platform and the six providers over it. The palette core registers them
 * from `providers/index.js` `registerCoreProviders()`.
 */
import createTasks from '../providers/tasks';
import createBoards from '../providers/boards';
import createDocuments from '../providers/documents';
import createRequests from '../providers/requests';
import createCollections from '../providers/collections';
import createCalendar from '../providers/calendar';
import { desktopIndexClient } from './indexClient';
import { createWebIndexClient } from './webIndexClient';
import { createBatchingClient } from './batch';
import { isWebApp } from '../../platform/target';
import { listRecords, onRecordsWritten } from '../../records/recordsClient';

export const INDEX_PROVIDER_FACTORIES = Object.freeze({
  tasks: createTasks,
  boards: createBoards,
  documents: createDocuments,
  requests: createRequests,
  collections: createCollections,
  calendar: createCalendar,
});

let client = null;

/**
 * The client for this platform, usable at once: on desktop the Tauri API
 * is imported on the first call, on web the in-memory fallback is used.
 */
export function getIndexClient() {
  if (client) return client;
  if (isWebApp()) {
    client = createWebIndexClient({ listRecords, onRecordsWritten });
    return client;
  }
  const real = desktopIndexClient();
  const later = (fn) => (...args) => real.then((c) => fn(c, ...args));
  const sub = (name) => (fn) => {
    let off = null;
    let gone = false;
    real.then((c) => {
      if (!gone) off = c[name](fn);
    });
    return () => {
      gone = true;
      off?.();
    };
  };
  // One keystroke → one palette_search over every index provider's kinds (batch.js).
  client = createBatchingClient({
    search: later((c, a, o) => c.search(a, o)),
    get: later((c, a, o) => c.get(a, o)),
    status: later((c) => c.status()),
    rebuild: later((c, a) => c.rebuild(a)),
    onProgress: sub('onProgress'),
    onReady: sub('onReady'),
  });
  return client;
}

/** The six index providers over `indexClient` (default: this platform's); `deps` are test seams. */
export function createIndexProviders(indexClient = getIndexClient(), deps = {}) {
  return Object.values(INDEX_PROVIDER_FACTORIES).map((create) => create({ ...deps, client: indexClient }));
}
